import './BotChatMessageExpertMode.styles.scss';

import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { useEventListener } from 'primereact/hooks';
import { InputText } from 'primereact/inputtext';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Tooltip } from 'primereact/tooltip';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useStompClient } from 'react-stomp-hooks';

import { ANTI_SPAM_DELAY } from '../../../constants';
import { Permission } from '../../../model/enumeration/Permission.enum';
import authStore from '../../../store/AuthStore';
import chatStore from '../../../store/ChatStore';
import expertModeStore from '../../../store/ExpertModeStore';
import notificationStore from '../../../store/NotificationStore';
import permissionStore from '../../../store/PermissionStore';
import playerStore from '../../../store/PlayerStore';
import userSettingsStore from '../../../store/UserSettingsStore';
import Bot from '../../Bot/Bot';
import {
  getEmojiImage,
  getEmojiValues,
  sendMessageToChat,
} from '../../Chat/chat.render.utils';
import { BotChatMessageExpertModeProps } from './BotChatMessageExpertModeProps';

const BotChatMessageExpertMode: FC<BotChatMessageExpertModeProps> = ({
  table,
}) => {
  const [text, setText] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const selectedBot = expertModeStore.getSelectedBot();

  const client = useStompClient();

  const chatMessages = useMemo(
    () => chatStore.getChatMessages(table.id),
    [chatStore.chatStateChanged],
  );
  const emojiPanel = useRef<OverlayPanel>(null);

  const hasChatPermission = useMemo(
    () => permissionStore.hasPermission(Permission.CHAT),
    [authStore.authenticatedUser],
  );

  const isChatDisabled =
    !hasChatPermission ||
    !selectedBot ||
    userSettingsStore.settings.chat.disabled;

  const inputTooltip = useMemo(() => {
    if (hasChatPermission && selectedBot) {
      return undefined;
    } else if (userSettingsStore.settings.chat.disabled) {
      return 'Chat is disabled in settings';
    } else if (!hasChatPermission) {
      return 'Chat is not available';
    } else {
      return 'Bot is not selected';
    }
  }, [hasChatPermission, selectedBot]);

  useEffect(() => {
    if (playerStore.findBots(table.players).length === 1) {
      expertModeStore.setSelectedBot(playerStore.findBots(table.players)[0]);
    }
    return () => expertModeStore.setSelectedBot(null);
  }, [table]);

  useEffect(() => {
    if (showErrorMessage) {
      notificationStore.showNotification({
        severity: 'error',
        summary: 'Error',
        detail: `Min delay between duplicated messages is ${
          ANTI_SPAM_DELAY / 1000
        } seconds`,
      });
      setShowErrorMessage(false);
    }
  }, [showErrorMessage]);

  const sendMessage = (textMessage: string, emoji: boolean): void => {
    if (isChatDisabled) {
      return;
    }
    sendMessageToChat(
      textMessage,
      emoji,
      chatMessages,
      selectedBot,
      table,
      client,
      setShowErrorMessage,
      setText,
    );
  };

  const [bindKeyDown, unbindKeyDown] = useEventListener({
    type: 'keydown',
    listener: (e: KeyboardEvent) => {
      if (e.code === 'Enter') {
        sendMessage(text, false);
      }
    },
  });

  useEffect(() => {
    bindKeyDown();
    return () => {
      unbindKeyDown();
    };
  }, [bindKeyDown, unbindKeyDown]);

  const emojiValues: string[] = useMemo(() => getEmojiValues(), []);

  const emojiContent = emojiValues?.map(emojiValue => (
    <span
      className="emoji cursor-pointer m-2"
      key={emojiValue}
      onClick={() => {
        sendMessage(emojiValue, true);
        emojiPanel.current?.hide();
      }}
    >
      {getEmojiImage(emojiValue)}
    </span>
  ));

  return (
    <div className="BotChatMessageExpertMode-Component">
      <Tooltip
        content={inputTooltip}
        target=".bot-chat-wrapper"
        position="bottom"
      />
      <div className="bot-chat-wrapper row">
        <InputText
          className="bot-chat-input"
          value={text}
          disabled={isChatDisabled}
          maxLength={200}
          onChange={e => setText(e.target.value)}
        />
        <div
          className="bot-chat-emoji"
          onMouseEnter={e =>
            hasChatPermission && selectedBot && emojiPanel.current?.toggle(e)
          }
          onMouseLeave={() => emojiPanel.current?.hide()}
        >
          <Button
            className="p-button-rounded p-button-text"
            icon="pi pi-face-smile"
            disabled={isChatDisabled}
          />
          <OverlayPanel
            ref={emojiPanel}
            dismissable={false}
            className="BotChatMessageExpertMode-Component-OverlayPanel"
          >
            <div className="emoji-panel">{emojiContent}</div>
          </OverlayPanel>
        </div>
        <Button
          className="bot-chat-btn"
          icon="pi pi-send"
          rounded
          text
          disabled={!text || isChatDisabled}
          severity="success"
          aria-label="send"
          onClick={() => sendMessage(text, false)}
        />
      </div>
      <div className="bots-wrapper">
        {playerStore.findBots(table.players).map(bot => (
          <Bot
            bot={bot}
            key={`bot-${bot.pid}`}
            selected={selectedBot?.pid === bot.pid}
            onSelectBot={expertModeStore.setSelectedBot}
          />
        ))}
      </div>
    </div>
  );
};

export default observer(BotChatMessageExpertMode);
